"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CalendarRange, ChartLine as LineChart, ShieldAlert, ArrowUpRight, SunMedium, Sparkles, TrendingUp } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const challenges = [{
  id: 1,
  title: "Event Surges & Booking Window Compression",
  subtitle: "Demand management",
  iconName: "CalendarRange",
  description: "Manage massive surges during Kanwar Yatra, Somvati Amavasya, and auspicious bathing dates against midweek lulls. We implement dynamic minimum length-of-stay controls and predictive inventory allocation to maximize event-driven RevPAR.",
  metricLabel: "Strategy focus",
  metricValue: "Pace-based pricing & event MLOS restrictions"
}, {
  id: 2,
  title: "Aggressive Corridor Rate Undercutting",
  subtitle: "Real-time rate intelligence",
  iconName: "LineChart",
  description: "Monitor high-frequency rate fluctuations across Har-ki-Pauri, Rishikesh Highway, and bypass hotel clusters. Automated competitive alerts and intraday price fencing prevent distress discounting while securing early high-value bookings.",
  metricLabel: "Strategy focus",
  metricValue: "Intraday OTA monitoring & rule-based rate fencing"
}, {
  id: 3,
  title: "OTA Dependency & Offline Wholesaler Leakage",
  subtitle: "Channel distribution",
  iconName: "ShieldAlert",
  description: "Stop margin erosion from unauthorized travel agent discounting and high OTA commissions. We build robust direct-booking engines for pilgrimage families, corporate retreats, and spiritual tour groups with unified rate parity.",
  metricLabel: "Strategy focus",
  metricValue: "Wholesaler rate fencing & direct channel boost"
}];
const seasonalityWindows = [{
  id: 1,
  period: "Peak Festival & Snan Dates",
  timing: "Kanwar Yatra, Kumbh/Ardh Cycles, Baisakhi, Kartik Purnima",
  strategy: "Strict minimum stay restrictions, premium non-refundable rate fences, and early inventory lock-in to capture peak ARR.",
  demandLevel: "Super Peak Demand"
}, {
  id: 2,
  period: "Weekend NCR Inflow & Spiritual Getaways",
  timing: "Friday to Sunday (Year-Round)",
  strategy: "Dynamic package bundling (Ganga Aarti transfers, temple logistics, wellness add-ons) and compressed lead-time yield escalations.",
  demandLevel: "High Recurring Demand"
}, {
  id: 3,
  period: "Midweek & Monsoonal Shoulder Periods",
  timing: "Monday to Thursday & Monsoon Lulls",
  strategy: "Targeted corporate offsites, senior-citizen spiritual retreats, and targeted meta-search promotions to sustain baseline occupancy.",
  demandLevel: "Base Yield Optimization"
}];
export default function Landscape() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.15
      }
    }
  };
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="hospitalitylandscapehardwar" data-nav="light" className="relative bg-background py-20 md:py-28 text-foreground">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Section Header */}
        <div className="mx-auto max-w-3xl text-center mb-16 md:mb-20">
          <Badge variant="outline" className="mb-4 border-secondary/40 text-secondary-foreground">
            Market Dynamics & Yield Protection
          </Badge>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-foreground">
            Navigating the hospitality landscape of Haridwar
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            Haridwar represents a high-velocity pilgrimage and spiritual tourism hub characterized by extreme event-driven demand spikes, compressed weekend booking windows from Delhi-NCR, and intense comp-set rivalry along the Ganges corridor. Sunil Singh structures disciplined, predictive revenue strategies tailored for Haridwar hotels, heritage ashrams, and boutique riverside properties.
          </p>
        </div>

        {/* 3-Column Card Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.15
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 mb-16">
          {challenges.map((item, index) => <motion.div key={item.id} data-index={index} variants={cardVariants} className="h-full flex">
              <Card className="flex flex-col justify-between w-full border border-border bg-card shadow-sm hover:shadow-md transition-[border-color,box-shadow] duration-300">
                <CardHeader className="gap-3">
                  <div className="flex items-center justify-between">
                    <div className="inline-flex size-12 items-center justify-center rounded-md bg-secondary/10 text-secondary-foreground">
                      {item.iconName === "CalendarRange" && <CalendarRange className="size-6 text-chart-2" />}
                      {item.iconName === "LineChart" && <LineChart className="size-6 text-chart-2" />}
                      {item.iconName === "ShieldAlert" && <ShieldAlert className="size-6 text-chart-2" />}
                    </div>
                    <span className="text-xs font-mono font-medium text-muted-foreground tracking-wider uppercase">
                      0{index + 1}
                    </span>
                  </div>
                  <div>
                    <span className="text-xs font-semibold uppercase tracking-wider text-secondary">
                      {item.subtitle}
                    </span>
                    <CardTitle className="font-serif text-xl sm:text-2xl font-bold mt-1.5 text-foreground leading-snug">
                      {item.title}
                    </CardTitle>
                  </div>
                </CardHeader>

                <CardContent className="space-y-6 pt-2">
                  <CardDescription className="text-sm leading-relaxed text-muted-foreground">
                    {item.description}
                  </CardDescription>

                  <div className="rounded-md border border-border/60 bg-muted/40 p-3.5 text-xs">
                    <p className="font-semibold text-foreground mb-1">
                      {item.metricLabel}
                    </p>
                    <p className="text-muted-foreground">
                      {item.metricValue}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>)}
        </motion.div>

        {/* Seasonality Dynamics Block */}
        <motion.div initial={{
        opacity: 0,
        y: shouldReduceMotion ? 0 : 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.15
      }} transition={{
        duration: 0.5,
        ease: "easeOut"
      }} className="rounded-xl border border-border bg-muted/20 p-6 sm:p-8 lg:p-10 mb-14">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 pb-6 border-b border-border/70 mb-6">
            <div>
              <div className="flex items-center gap-2 mb-1.5">
                <SunMedium className="size-4 text-chart-2" />
                <span className="text-xs font-mono font-semibold uppercase tracking-wider text-secondary">
                  Demand Forecasting Matrix
                </span>
              </div>
              <h3 className="text-xl sm:text-2xl font-serif font-bold text-foreground">
                Haridwar Seasonality & Yield Calendar
              </h3>
            </div>
            <p className="text-xs sm:text-sm text-muted-foreground max-w-md">
              Converting unpredictable spiritual calendar spikes into structured, high-margin revenue cycles.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {seasonalityWindows.map((season, i) => <div key={season.id} data-index={i} className="bg-card border border-border/70 rounded-lg p-5 flex flex-col justify-between">
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <Badge variant="secondary" className="text-[11px] font-medium bg-secondary/15 text-secondary-foreground border-transparent">
                      {season.demandLevel}
                    </Badge>
                    {i === 0 && <Sparkles className="size-4 text-chart-2" />}
                    {i === 1 && <TrendingUp className="size-4 text-chart-2" />}
                    {i === 2 && <CalendarRange className="size-4 text-chart-2" />}
                  </div>
                  <h4 className="text-base font-bold text-foreground mb-1">
                    {season.period}
                  </h4>
                  <p className="text-xs font-medium text-secondary mb-3">
                    {season.timing}
                  </p>
                </div>
                <p className="text-xs leading-relaxed text-muted-foreground border-t border-border/50 pt-3">
                  {season.strategy}
                </p>
              </div>)}
          </div>
        </motion.div>

        {/* Section Bottom CTA / Footnote */}
        <div className="pt-8 border-t border-border/70 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-sm text-muted-foreground text-center sm:text-left">
            Need a tailored revenue assessment for your Haridwar hotel or boutique stay?
          </p>
          <Button asChild variant="default" className="bg-primary text-primary-foreground hover:bg-primary/90">
            <Link to="#" className="inline-flex items-center gap-2">
              <span>Book a Revenue Audit</span>
              <ArrowUpRight className="size-4" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}